import React, { useEffect, useRef, useState } from 'react';
import '../../../../style_sheets/carousel.scss';

import GenericContent from './GenericContent';
import CodeSnippetElement from './CodeSnippetElement';
import AccordionContent from './AccordionContent';
import EmbeddableContent from './EmbeddableContent';
import VideoElement from './VideoElement';

const components = {
  GenericContent,
  CodeSnippetElement,
  AccordionContent,
  EmbeddableContent,
  VideoElement,
};

const CarouselElement = ({ elements, title, description }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [topNavIsSticky, setTopNavIsSticky] = useState(false);

  const carouselRef = useRef(null);
  const stickyMarkerRef = useRef(null);

  useEffect(() => {
    const marker = stickyMarkerRef.current;

    if (!marker) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setTopNavIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
      }
    );

    observer.observe(marker);

    return () => observer.disconnect();
  }, []);

  if (!elements || elements.length === 0) {
    return null;
  }

  const goToNext = () => {
    setCurrentIndex((index) => (index < elements.length - 1 ? index + 1 : 0));
  };

  const goToPrev = () => {
    setCurrentIndex((index) => (index > 0 ? index - 1 : elements.length - 1));
  };

  const scrollToCarouselTop = () => {
    requestAnimationFrame(() => {
      carouselRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const goToNextAndScroll = () => {
    goToNext();
    scrollToCarouselTop();
  };

  const goToPrevAndScroll = () => {
    goToPrev();
    scrollToCarouselTop();
  };

  return (
    <div className="carousel-element" ref={carouselRef}>
      <div className="carousel-heading">
        <h2 className="marginless">{title}</h2>

        {description && (
          <div
            className="trix-content"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>

      <div
        className="carousel-sticky-marker"
        ref={stickyMarkerRef}
        aria-hidden="true"
      />

      <CarouselNavigation
        currentIndex={currentIndex}
        totalSteps={elements.length}
        goToPrev={goToPrevAndScroll}
        goToNext={goToNextAndScroll}
        position="top"
        isSticky={topNavIsSticky}
      />

      <div className="carousel-viewport">
        <CarouselItem
          key={`${elements[currentIndex].type}-${elements[currentIndex].id}`}
          element={elements[currentIndex]}
        />
      </div>

      <div className="carousel-navigation carousel-navigation-bottom">
        <button
          type="button"
          className={`carousel-button carousel-button-next ${
            currentIndex === elements.length - 1 ? 'carousel-button-return' : ''
          }`}
          onClick={goToNextAndScroll}
          aria-label={
            currentIndex === elements.length - 1
              ? 'Return to the first slide'
              : 'Show next step and return to the top of the carousel'
          }
        >
          <span>
            {currentIndex === elements.length - 1
              ? 'Return to First Slide'
              : 'Next'}
          </span>
          <i className="bx bx-chevron-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const CarouselNavigation = ({
  currentIndex,
  totalSteps,
  goToPrev,
  goToNext,
  position,
  isSticky,
}) => {
  const stickyClass = isSticky ? 'is-sticky' : '';

  return (
    <div
      className={`carousel-navigation carousel-navigation-${position} ${stickyClass}`}
      aria-label={`${position} carousel navigation`}
    >
      <button
        type="button"
        className="carousel-button carousel-button-prev"
        onClick={goToPrev}
        aria-label="Show previous step"
      >
        <i className="bx bx-chevron-left" aria-hidden="true" />
        <span>Previous</span>
      </button>

      <div className="carousel-progress" aria-live="polite" aria-atomic="true">
        <span className="carousel-progress-label">
          Step {currentIndex + 1} of {totalSteps}
        </span>

        <div className="carousel-dots" aria-hidden="true">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <span
              key={index}
              className={`carousel-dot ${
                index === currentIndex ? 'is-active' : ''
              }`}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        className={`carousel-button carousel-button-next ${
          currentIndex === totalSteps - 1 ? 'carousel-button-return' : ''
        }`}
        onClick={goToNext}
        aria-label={
          currentIndex === totalSteps - 1
            ? 'Return to the first slide'
            : 'Show next step'
        }
      >
        <span>
          {currentIndex === totalSteps - 1 ? 'Return to First Slide' : 'Next'}
        </span>
        <i className="bx bx-chevron-right" aria-hidden="true" />
      </button>
    </div>
  );
};

const CarouselItem = ({ element }) => {
  const ElementName = components[element.type];

  if (!ElementName) {
    return null;
  }

  return (
    <ElementName
      title={element.title}
      title_color={element.title_color}
      body={element.body}
      description={element.description}
      content={element.content}
      language={element.language}
      url={element.url}
      caption_url={element.caption_url}
      video_url={element.video_url}
      top_description={element.top_description}
      bottom_description={element.bottom_description}
      autoplay={element.autoplay}
      looping={element.looping}
    />
  );
};

export default CarouselElement;
